import React, { useEffect } from "react";
import { 
  CloseRounded, 
  GitHub, 
  LinkedIn, 
  Launch, 
  CalendarToday,
  Code,
  People,
  Category
} from "@mui/icons-material";
import { Modal, IconButton, Chip } from "@mui/material";
import styled from "styled-components";

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Wrapper = styled.div`
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  background: ${({ theme }) => theme.card};
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  border: 1px solid ${({ theme }) => theme.primary + 30};
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideUp {
    from { transform: translateY(50px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @media (max-width: 768px) {
    max-height: 95vh;
  }
`;

const Header = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 150px;
  }
`;

const HeaderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.7);
`;

const CloseButton = styled(IconButton)`
  position: absolute !important;
  top: 16px !important;
  right: 16px !important;
  background: rgba(0, 0, 0, 0.7) !important;
  color: white !important;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(0, 0, 0, 0.9) !important;
  }
`;

const Content = styled.div`
  padding: 32px;
  overflow-y: auto;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const TitleSection = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: ${({ theme }) => theme.text_primary};
  margin: 0 0 12px 0;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const MetaSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
  font-weight: 500;
`;

const CategoryBadge = styled.div`
  background: ${({ theme, category }) => 
    category === 'web app' ? theme.primary : 
    category === 'machine learning' ? '#10B981' : 
    theme.secondary};
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 1.7;
color: ${({ theme }) => theme.text_primary + 'DD'};
  margin-bottom: 32px;
  white-space: pre-line;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  & svg {
    color: ${({ theme }) => theme.primary};
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 32px;
`;

const TechTag = styled(Chip)`
  && {
    background: ${({ theme }) => theme.primary + 15};
    color: ${({ theme }) => theme.primary};
    font-weight: 500;
    border: 1px solid ${({ theme }) => theme.primary + 30};
    
    &:hover {
      background: ${({ theme }) => theme.primary + 25};
    }
  }
`;

const MembersSection = styled.div`
  margin-bottom: 32px;
`;

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const MemberCard = styled.div`
  background: ${({ theme }) => theme.bgLight};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid ${({ theme }) => theme.primary + 20};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const MemberAvatar = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${({ theme }) => theme.primary};
`;

const MemberInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 4px;
`;

const MemberRole = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 8px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialLink = styled.a`
  color: ${({ theme }) => theme.text_secondary};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 40px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  min-width: 200px;
  
  ${({ variant, theme }) => 
    variant === 'primary' ? `
      background: linear-gradient(135deg, ${theme.primary}, ${theme.secondary});
      color: white;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 25px ${theme.primary + 40};
      }
    ` : `
      background: ${theme.bgLight};
      color: ${theme.text_primary};
      border: 2px solid ${theme.primary};
      
      &:hover {
        background: ${theme.primary + 15};
        transform: translateY(-2px);
      }
    `
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProjectDetails = ({ openModal, setOpenModal }) => {
  const project = openModal?.project;
  
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setOpenModal({ state: false, project: null });
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [setOpenModal]);
  
  if (!project) return null;
  
  return (
    <Modal
      open={true}
      onClose={() => setOpenModal({ state: false, project: null })}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <Container onClick={(e) => e.target === e.currentTarget && setOpenModal({ state: false, project: null })}>
        <Wrapper>
          <Header>
            <HeaderImage src={project.image} alt={project.title} />
            <CloseButton onClick={() => setOpenModal({ state: false, project: null })}>
              <CloseRounded />
            </CloseButton>
          </Header>
          
          <Content>
            <TitleSection>
              <Title>{project.title}</Title>
              <MetaSection>
                <MetaItem>
                  <CalendarToday sx={{ fontSize: 16 }} />
                  {project.date}
                </MetaItem>
                <CategoryBadge category={project.category}>
                  <Category sx={{ fontSize: 14, marginRight: '4px' }} />
                  {project.category === 'web app' ? 'Web Application' : 
                   project.category === 'machine learning' ? 'ML Project' : project.category}
                </CategoryBadge>
              </MetaSection>
            </TitleSection>
            
            <Description>{project.description}</Description>
            
            <SectionTitle><Code /> Technologies Used</SectionTitle>
            <TagsContainer>
              {project.tags?.map((tag, index) => (
                <TechTag key={index} label={tag} />
              ))}
            </TagsContainer>
            
            {project.member && project.member.length > 0 && (
              <>
                <SectionTitle><People /> Team Members</SectionTitle>
                <MembersSection>
                  <MembersGrid>
                    {project.member.map((member, index) => (
                      <MemberCard key={index}>
                        <MemberAvatar src={member.img} alt={member.name} />
                        <MemberInfo>
                          <MemberName>{member.name}</MemberName>
                          {member.role && <MemberRole>{member.role}</MemberRole>}
                          <SocialLinks>
                            {member.github && (
                              <SocialLink href={member.github} target="_blank" rel="noopener noreferrer">
                                <GitHub sx={{ fontSize: 20 }} />
                              </SocialLink>
                            )}
                            {member.linkedin && (
                              <SocialLink href={member.linkedin} target="_blank" rel="noopener noreferrer">
                                <LinkedIn sx={{ fontSize: 20 }} />
                              </SocialLink>
                            )}
                          </SocialLinks>
                        </MemberInfo>
                      </MemberCard>
                    ))}
                  </MembersGrid>
                </MembersSection>
              </>
            )}
            
            <Actions>
              {project.github && (
                <ActionButton 
                  href={project.github} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  variant="secondary"
                >
                  <GitHub /> View Source Code
                </ActionButton>
              )}
              
              {project.webapp && (
                <ActionButton 
                  href={project.webapp} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  variant="primary"
                >
                  <Launch /> Live Demo
                </ActionButton>
              )}
              
              {project.demo && !project.webapp && (
                <ActionButton 
                  href={project.demo} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  variant="primary"
                >
                  <Launch /> View Demo
                </ActionButton>
              )}
            </Actions>
          </Content>
        </Wrapper>
      </Container>
    </Modal>
  );
};

export default ProjectDetails;